import Layout from "../Layout/Layout";
import ImageGallery from "../../components/ImageGallery/ImageGallery";
import { FaTrailer } from "react-icons/fa6";
import { PiToilet, PiOven } from "react-icons/pi";
import { TbTruckDelivery, TbRating21Plus } from "react-icons/tb";
import {
  MdPets,
  MdOutlineBed,
  MdBatteryChargingFull,
  MdSunnySnowing,
} from "react-icons/md";
import YouTubeVideo from "../../components/Youtube/Youtube";
import { JsonLDData } from "react-safe-json-ld";
import { logViewItem } from "../../lib/analytics";
import React from "react";
import FAQs from "../../components/FAQs/FAQs";
import { FAQItem } from "../../components/FAQs/FAQItem";
import WheelbaseCheckout from "../../components/Wheelbase/Checkout";

const CommodoreDetail = () => {
  React.useEffect(() => {
    logViewItem("Commodore");
  }, []);

  const FAQData: FAQItem[] = [
    {
      question: "What is the Commodore best suited for?",
      answer:
        "Adventure road trips, sightseeing circuits, state and national parks, family visits, and flexible multi-stop travel across Texas and beyond — a comfortable alternative to hotels when you want mobility and your own space.",
    },
    {
      question: "How many people can Commodore sleep?",
      answer:
        "Commodore comfortably sleeps 1-2 people on the pull out Twin XL bed.",
    },
    {
      question: "Can I take Commodore off-grid?",
      answer:
        "Yes. Commodore has onboard batteries and carries fresh water for self-contained travel. A roof top solar panel is being added for stronger off-grid capability.",
    },
    {
      question: "Is Commodore good for kayaking and fishing trips?",
      answer:
        "Yes, Commodore can handle a limited amount of rooftop storage and has a frame mounted receiver for carrying additional equipment via a trailer.",
    },
    {
      question: "Does Commodore have towing capability?",
      answer:
        "Yes, the frame mounted receiver is capable of towing up to 10,000 lbs.",
    },
    {
      question: "Where can I pick up Commodore?",
      answer: "We are based in Richardson, TX and we can deliver within 50 miles.",
    },
  ];

  const galleryImages: string[] = [
    "/images/fleet/commodore/commodore-photos-exterior-front-05.jpg",
    "/images/fleet/commodore/commodore-photos-exterior-side-08.jpg",
    "/images/fleet/commodore/commodore-photos-exterior-side-03.jpg",
    "/images/fleet/commodore/commodore-photos-exterior-rear-06.jpg",
    "/images/fleet/commodore/commodore-photos-exterior-front-08.jpg",
    "/images/fleet/commodore/commodore-photos-exterior-rear-passenger-side-07.jpg",
    "/images/fleet/commodore/commodore-photos-interior-galley.jpg",
    "/images/fleet/commodore/commodore-photos-interior-lounge.jpg",
    "/images/fleet/commodore/commodore-photos-interior-bed.jpg",
  ];

  const images: string[] = galleryImages.map(
    (src) => `https://www.earthboundmartian.com${src}`
  );

  const structuredData: JsonLDData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Product",
        name: "Commodore — Adventure-Ready Landship",
        description:
          "Commodore is a comfortable, self-contained Ford E-350 Landship built for real travel and adventures. Guests use it for adventure road trips, sightseeing, family visits, and flexible multi-stop travel across Texas and beyond. Functional kitchen, sleeps two, reliable power, and climate control with Zero Breeze Mk 3.",
        brand: { "@type": "Brand", name: "Earthbound Martian" },
        image: images,
        offers: { "@type": "Offer", priceCurrency: "USD", price: "110" },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.8",
          reviewCount: "12",
        },
      },
      {
        "@type": "FAQPage",
        mainEntity: FAQData.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.answer,
          },
        })),
      },
    ],
  };

  return (
    <Layout
      title="Commodore — Adventure-Ready Landship | Earthbound Martian"
      structuredData={structuredData}
    >
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Hero Image + Title */}
        <div className="relative rounded-3xl overflow-hidden mb-12">
          <img
            src="/images/fleet/commodore/commodore-photos-exterior-front-05.jpg"
            alt="Commodore"
            className="w-full h-[500px] object-cover object-bottom"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="absolute bottom-10 left-10 text-white">
            <h1 className="text-6xl font-bold">Commodore</h1>
            <p className="text-2xl mt-2">Adventure-Ready Landship</p>
          </div>
        </div>

        {/* Quick Icons */}
        <div className="flex flex-wrap gap-6 mb-12">
          <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-2xl shadow">
            <span className="text-3xl">
              <TbTruckDelivery size={24} />
            </span>
            <div>
              <div className="font-semibold">Delivery Available</div>
              <div className="text-md text-zinc-500">Greater DFW Area</div>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-2xl shadow">
            <span className="text-3xl">
              <MdPets size={24} />
            </span>
            <div className="font-semibold">Pet Friendly</div>
          </div>
          <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-2xl shadow">
            <span className="text-3xl">
              <TbRating21Plus size={24} />
            </span>
            <div className="font-semibold">21+</div>
          </div>
          <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-2xl shadow">
            <span className="text-3xl">
              <MdOutlineBed size={24} />
            </span>
            <div>
              <div className="font-semibold">Twin-XL</div>
              <div className="text-md text-zinc-500">Sleeps 2 Cozy</div>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-2xl shadow">
            <span className="text-3xl">
              <FaTrailer size={24} />
            </span>
            <div>
              <div className="font-semibold">Towing</div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <div className="text-xl text-zinc-600 leading-relaxed mb-10 space-y-6">
              <p>
                A comfortable, self-contained Landship built for real travel and
                adventures.
              </p>
              <p>
                Commodore is our original Ford E-350 conversion, designed for
                people who want to move freely across Texas and beyond without
                the cost and rigidity of hotels. Guests use it for adventure
                road trips, sightseeing circuits, family visits, and flexible
                multi-stop travel.
              </p>

              <div>
                <h3 className="font-semibold text-xl text-zinc-800 mb-3">
                  What it’s good for
                </h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    Adventure trips (state parks, national parks, longer
                    overland routes)
                  </li>
                  <li>Sightseeing and regional exploration</li>
                  <li>
                    Comfortable alternative to hotels when you want mobility and
                    your own space
                  </li>
                  <li>
                    Small groups or couples who value simplicity and reliability
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-xl text-zinc-800 mb-3">
                  Key features
                </h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Functional kitchen and efficient systems</li>
                  <li>Comfortable sleeping for two</li>
                  <li>Reliable power setup</li>
                  <li>
                    Comfortable climate control with integrated Zero Breeze Mk 3
                  </li>
                  <li>Easy to drive and park compared to larger RVs</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-xl text-zinc-800 mb-3">
                  Recent &amp; upcoming upgrades
                </h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    Full roof solar panel installation for stronger off-grid
                    capability and better radiative heat protection.
                  </li>
                  <li>
                    Tighter integration of the Zero Breeze Mk 3 air conditioner
                    for better cooling on warm Texas days.
                  </li>
                </ul>
              </div>

              <p>
                Commodore has already taken guests from the DFW area to places
                like the Grand Canyon, Redwood National Park, Colorado, New
                Mexico, and around Texas. It's built to take people like you on
                adventures.
              </p>
              <p className="font-medium text-zinc-800">Where will you go?</p>
            </div>

            <div className="mb-10">
              <YouTubeVideo videoId="fGTjFQumbPY" />
            </div>

            {/* Vehicle Info */}
            <div className="mb-12">
              <h3 className="font-semibold text-xl mb-6">Vehicle Info</h3>
              <div className="grid grid-cols-2 gap-x-12 gap-y-4 text-sm">
                <div>
                  <span className="text-lg text-zinc-500">Length: 20 ft</span>
                </div>
                <div>
                  <span className="text-lg text-zinc-500">
                    Transmission: Automatic
                  </span>
                </div>
                <div>
                  <span className="text-lg text-zinc-500">Fuel Type: Gas</span>
                </div>
                <div>
                  <span className="text-lg text-zinc-500">Range: ~ 350 
                  miles</span>
                </div>
                <div>
                  <span className="text-lg text-zinc-500">Weight: 10,000 lbs</span>
                  
                </div>
                <div>
                  <span className="text-lg text-zinc-500">Sleeps: 1-2</span>
                </div>
              </div>
            </div>

            {/* Amenities */}
            <div className="mb-12">
              <h3 className="font-semibold text-xl mb-8">Amenities</h3>

              <div className="grid md:grid-cols-2 gap-x-16 gap-y-10">
                {/* Bathroom */}
                <div>
                  <h4 className="font-medium text-mars mb-4 flex items-center gap-3">
                    <span className="text-2xl">
                      <PiToilet size={24} />
                    </span>{" "}
                    Bathroom
                  </h4>
                  <ul className="text-lg space-y-2 text-zinc-600">
                    <li>• Cassette Toilet</li>
                    <li>• Toilet Paper</li>
                  </ul>
                </div>

                {/* Kitchen */}
                <div>
                  <h4 className="font-medium text-mars mb-4 flex items-center gap-3">
                    <span className="text-2xl">
                      <PiOven size={24} />
                    </span>{" "}
                    Kitchen
                  </h4>
                  <ul className="text-lg space-y-2 text-zinc-600">
                    <li>• Propane stove with 2 burners</li>
                    <li>• Sink with running water</li>
                    <li>• Compact fridge</li>
                    <li>• 10 Gallon Fresh Water Tank</li>
                  </ul>
                </div>

                {/* Power & Hookups */}
                <div>
                  <h4 className="font-medium text-mars mb-4 flex items-center gap-3">
                    <span className="text-2xl">
                      <MdBatteryChargingFull />
                    </span>{" "}
                    Power & Hookups
                  </h4>
                  <ul className="text-lg space-y-2 text-zinc-600">
                    <li>• Battery system</li>
                    <li>• Shore power hookup</li>
                    <li>• Inverter</li>
                  </ul>
                </div>

                {/* Climate */}
                <div>
                  <h4 className="font-medium text-mars mb-4 flex items-center gap-3">
                    <span className="text-2xl">
                      <MdSunnySnowing />{" "}
                    </span>{" "}
                    Climate & Comfort
                  </h4>
                  <ul className="text-lg space-y-2 text-zinc-600">
                    <li>• Zero Breeze Mk 3 air conditioner</li>
                    <li>• Thinsulate insulation</li>
                    <li>• Thermal curtains</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:sticky lg:top-24 h-fit overflow-visible z-20">
            <WheelbaseCheckout rig="commodore" />
          </div>
        </div>

        {/* Full Gallery Section */}
        <div className="mt-24">
          <h3 className="text-2xl font-semibold mb-8">Full Tour</h3>
          <ImageGallery images={galleryImages} />
        </div>

        <section className="flex mt-10 p-5 bg-zinc-100 rounded-xl">
          <FAQs faqs={FAQData} />
        </section>
      </div>
    </Layout>
  );
};

export default CommodoreDetail;
